export interface AnthropicModelPricing {
    inputCostPerToken: number; // USD per token
    outputCostPerToken: number; // USD per token
}

// Prices from https://www.anthropic.com/pricing in $/token
export const ANTHROPIC_PRICING: Record<string, AnthropicModelPricing> = {
    // Claude Opus 4.6
    "claude-opus-4-6": { inputCostPerToken: 5.0 / 1_000_000, outputCostPerToken: 25.0 / 1_000_000 },

    // Claude Opus 4.5
    "claude-opus-4-5": { inputCostPerToken: 5.0 / 1_000_000, outputCostPerToken: 25.0 / 1_000_000 },

    // Claude Opus 4.1
    "claude-opus-4-1": {
        inputCostPerToken: 15.0 / 1_000_000,
        outputCostPerToken: 75.0 / 1_000_000
    },

    // Claude Opus 4
    "claude-opus-4": { inputCostPerToken: 15.0 / 1_000_000, outputCostPerToken: 75.0 / 1_000_000 },

    // Claude Sonnet 4.6
    "claude-sonnet-4-6": {
        inputCostPerToken: 3.0 / 1_000_000,
        outputCostPerToken: 15.0 / 1_000_000
    },

    // Claude Sonnet 4.5
    "claude-sonnet-4-5": {
        inputCostPerToken: 3.0 / 1_000_000,
        outputCostPerToken: 15.0 / 1_000_000
    },

    // Claude Sonnet 4
    "claude-sonnet-4": { inputCostPerToken: 3.0 / 1_000_000, outputCostPerToken: 15.0 / 1_000_000 },

    // Claude Haiku 4.5
    "claude-haiku-4-5": { inputCostPerToken: 0.8 / 1_000_000, outputCostPerToken: 4.0 / 1_000_000 }
};

/**
 * Convert cost per token (USD) to Roboflow credits conversion rate.
 * Same formula as OpenAI/Gemini: (cost * margin) / credit_price
 */
export function priceToCreditConversion(costPerTokenUSD: number): number {
    const CREDIT_PRICE_USD = 3;
    const MARGIN = 1.5;
    return (costPerTokenUSD * MARGIN) / CREDIT_PRICE_USD;
}

/**
 * Slugify an Anthropic model name for use in billing feature names.
 * Converts "claude-sonnet-4-5" -> "claude-sonnet-4-5"
 * Converts "claude-sonnet-4.5" -> "claude-sonnet-4-5"
 */
export function slugifyAnthropicModel(model: string): string {
    return model.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
}

/**
 * Get conversion rate for a specific Anthropic model and token type.
 * Returns null if model is not found.
 */
export function getConversionRateForAnthropicModel(
    model: string,
    type: "input" | "output"
): number | null {
    const slug = slugifyAnthropicModel(model);
    const pricing = ANTHROPIC_PRICING[slug];
    if (!pricing) return null;
    const cost = type === "input" ? pricing.inputCostPerToken : pricing.outputCostPerToken;
    return priceToCreditConversion(cost);
}
